import { isFixtureMode } from "./memberService";
import { invokeFunction, supabase } from "./supabase";
import type {
  ControlledRunMetadata,
  CompensationModel,
  EarningSummary,
  PayoutRequest,
  PayoutRequestInput,
  ProductBrief,
  AssignmentTarget,
  TargetEventType,
  TaskAssignment,
  TaskOffer,
  TrainingItem,
} from "./workflowTypes";
import type { ControlledReviewMode, ControlledRunRisk, ControlledRunType, ProofAutoDecision } from "./proofAutomation";

async function fixtures() {
  if (!import.meta.env.DEV) throw new Error("Development fixtures are unavailable in production.");
  return import("./workflowFixtures");
}

export async function listTaskOffers(): Promise<TaskOffer[]> {
  if (isFixtureMode) return (await fixtures()).listFixtureOffers();
  const { data, error } = await supabase!.from("member_task_offers").select("*").order("matched", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(mapTaskOffer);
}

export async function getActiveTask(): Promise<TaskAssignment | null> {
  if (isFixtureMode) return (await fixtures()).getFixtureActiveTask();
  const { data, error } = await supabase!.from("member_active_task").select("*").maybeSingle();
  if (error) throw error;
  return data ? mapAssignment(data) : null;
}

export async function getAdminQaActiveTask(qaRunId: string): Promise<TaskAssignment | null> {
  if (isFixtureMode) {
    const qa = await import("./adminQaService");
    return qa.getFixtureQaActiveTask(qaRunId);
  }
  const { data, error } = await supabase!.rpc("admin_qa_active_task", { p_qa_run_id: qaRunId }).maybeSingle();
  if (error) throw error;
  return data ? mapAssignment(data as Record<string, unknown>) : null;
}

export async function acceptTaskOffer(offerId: string) {
  if (isFixtureMode) return (await fixtures()).acceptFixtureOffer(offerId);
  const { error } = await supabase!.rpc("accept_task_offer", { p_offer_id: offerId });
  if (error) throw error;
}

export async function saveTaskProgress(assignmentId: string, stepKey: string, value: boolean | string | number) {
  if (isFixtureMode) return (await fixtures()).saveFixtureProgress(stepKey, value);
  const { error } = await supabase!.rpc("save_task_step_progress", {
    p_assignment_id: assignmentId,
    p_step_key: stepKey,
    p_value: value,
  });
  if (error) throw error;
}

export async function saveTaskEvidence(
  assignmentId: string,
  requirementKey: string,
  value: string,
  kind: "text" | "url" | "metric" | "screenshot" | "file" = "text",
) {
  if (isFixtureMode) return (await fixtures()).saveFixtureEvidence(requirementKey, value);
  const evidence = readEvidenceDraft(assignmentId);
  evidence[requirementKey] = value;
  localStorage.setItem(`house-evidence-${assignmentId}`, JSON.stringify(evidence));
  const payload = kind === "url"
    ? { kind, urlValue: value }
    : { kind, textValue: value };
  const { error } = await supabase!.rpc("save_task_evidence", {
    p_assignment_id: assignmentId,
    p_requirement_key: requirementKey,
    p_value: payload,
  });
  if (error) throw error;
}

export async function uploadTaskEvidence(
  assignmentId: string,
  requirementKey: string,
  file: File,
  kind: "screenshot" | "file",
) {
  if (file.size > 10 * 1024 * 1024) throw new Error("Evidence files must be 10 MB or smaller.");
  if (isFixtureMode) {
    (await fixtures()).saveFixtureEvidence(requirementKey, file.name);
    return file.name;
  }
  const { data: auth } = await supabase!.auth.getUser();
  if (!auth.user) throw new Error("Authentication required.");
  const safeName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `${auth.user.id}/${assignmentId}/${crypto.randomUUID()}-${safeName}`;
  const { error: uploadError } = await supabase!.storage.from("work-evidence").upload(storagePath, file, { upsert: false });
  if (uploadError) throw uploadError;
  const { error } = await supabase!.rpc("save_task_evidence", {
    p_assignment_id: assignmentId,
    p_requirement_key: requirementKey,
    p_value: { kind, storagePath, metadata: { fileName: file.name, size: file.size, mimeType: file.type } },
  });
  if (error) {
    await supabase!.storage.from("work-evidence").remove([storagePath]);
    throw error;
  }
  const evidence = readEvidenceDraft(assignmentId);
  evidence[requirementKey] = file.name;
  localStorage.setItem(`house-evidence-${assignmentId}`, JSON.stringify(evidence));
  return file.name;
}

export async function finalizeTask(assignmentId: string) {
  if (isFixtureMode) return (await fixtures()).submitFixtureTask();
  const active = await getActiveTask();
  if (!active || active.assignmentId !== assignmentId) throw new Error("Active assignment not found.");
  const evidence = readEvidenceDraft(assignmentId);
  const { error } = await supabase!.rpc("finalize_task_submission", {
    p_assignment_id: assignmentId,
    p_evidence: { summary: evidence.result_note ?? "Task completed with the required proof.", items: [] },
  });
  if (error) throw error;
  localStorage.removeItem(`house-evidence-${assignmentId}`);
}

export async function finalizeQaTask(qaRunId: string, assignmentId: string, evidence: Record<string, string>) {
  if (isFixtureMode) {
    const qa = await import("./adminQaService");
    await qa.submitQaProof(qaRunId, evidence);
    return;
  }
  const { error } = await supabase!.rpc("admin_submit_qa_proof", {
    qaRunId,
    evidence: Object.keys(evidence).length ? evidence : readEvidenceDraft(assignmentId),
  });
  if (error) throw error;
  localStorage.removeItem(`house-evidence-${assignmentId}`);
}

export async function releaseTask(assignmentId: string) {
  if (isFixtureMode) return (await fixtures()).releaseFixtureTask();
  const { error } = await supabase!.rpc("release_task_assignment", { p_assignment_id: assignmentId });
  if (error) throw error;
}

export async function saveTargetEvent(assignmentTargetId: string, eventType: TargetEventType, payload: Record<string, string | number | boolean>) {
  if (isFixtureMode) return (await fixtures()).saveFixtureTargetEvent(assignmentTargetId, eventType, payload);
  const { error } = await supabase!.rpc("save_target_event", {
    p_assignment_target_id: assignmentTargetId,
    p_event_type: eventType,
    p_payload: payload,
  });
  if (error) throw error;
}

export async function submitMetricSnapshot(assignmentTargetId: string, platform: string, metricUrl: string, views: number, proof: Record<string, string>) {
  if (isFixtureMode) return (await fixtures()).saveFixtureMetricSnapshot(assignmentTargetId, platform, metricUrl, views, proof);
  const { error } = await supabase!.rpc("submit_metric_snapshot", {
    p_assignment_target_id: assignmentTargetId,
    p_platform: platform,
    p_metric_url: metricUrl,
    p_views: views,
    p_proof: proof,
  });
  if (error) throw error;
}

export async function listTraining(): Promise<TrainingItem[]> {
  if (isFixtureMode) return (await fixtures()).fixtureTraining;
  const { data, error } = await supabase!.from("member_training_library").select("*").order("minimum_level");
  if (error) throw error;
  return (data ?? []).map((item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    route: item.route,
    minimumLevel: item.minimum_level,
    kind: item.kind,
    url: item.resource_url,
    locked: item.locked,
  }));
}

export async function getEarningSummary(): Promise<EarningSummary> {
  if (isFixtureMode) return (await fixtures()).getFixtureEarnings();
  const { data, error } = await supabase!.from("member_earning_summary").select("*").single();
  if (error) throw error;
  return {
    pendingUsd: Number(data.pending_usd),
    availableUsd: Number(data.available_usd),
    requestedUsd: Number(data.requested_usd),
    paidUsd: Number(data.paid_usd),
    minimumPayoutUsd: Number(data.minimum_payout_usd),
    guardianRequired: Boolean(data.guardian_required),
  };
}

export async function listPayoutRequests(): Promise<PayoutRequest[]> {
  if (isFixtureMode) return (await fixtures()).listFixturePayouts();
  const { data, error } = await supabase!.from("payout_requests").select("*").order("requested_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((item) => ({
    id: item.id,
    amountUsd: Number(item.amount_usd),
    method: item.method,
    destinationMasked: item.destination_masked,
    status: item.status,
    requestedAt: item.requested_at,
    providerReference: item.provider_reference,
    guardianRequired: item.guardian_required,
    guardianApproved: item.guardian_approved,
  }));
}

export async function requestPayout(input: PayoutRequestInput, guardianRequired: boolean) {
  if (isFixtureMode) return (await fixtures()).requestFixturePayout(input, guardianRequired);
  await invokeFunction("request-payout", input);
}

function mapTaskOffer(row: Record<string, unknown>): TaskOffer {
  const snapshot = row.snapshot as TaskOffer | undefined;
  if (snapshot) return { ...snapshot, id: String(row.id), matched: Boolean(row.matched) };
  const graph = asObject(row.playbook_graph);
  const metadata = asObject(graph.metadata);
  const nodes = asArray(graph.nodes);
  const steps = nodes.map(mapGraphStep);
  const evidenceRequirements = collectEvidence(nodes);
  const rewardUsd = Number(row.reward_max ?? 0);
  return {
    id: String(row.id),
    taskId: String(row.workflow_id),
    title: String(row.brand ?? row.playbook_title ?? "House task"),
    summary: String(row.public_summary ?? row.playbook_summary ?? ""),
    route: row.route_required as TaskOffer["route"],
    outcome: row.outcome as TaskOffer["outcome"],
    rewardUsd,
    estimatedMinutes: Number(metadata.estimatedMinutes ?? Math.max(20, steps.length * 15)),
    dueAt: String(row.expires_at ?? new Date(Date.now() + 86_400_000).toISOString()),
    offerExpiresAt: row.expires_at ? String(row.expires_at) : undefined,
    matched: String(row.offer_type) === "matched",
    minimumLevel: row.minimum_level == null ? undefined : Number(row.minimum_level),
    minimumTrust: row.minimum_trust == null ? undefined : Number(row.minimum_trust),
    sopTitle: String(row.playbook_title ?? "Task SOP"),
    sopVersion: Number(graph.version ?? 1),
    resources: collectResources(nodes),
    steps,
    evidenceRequirements,
    product: mapProduct(row, metadata),
    compensation: mapCompensation(metadata, evidenceRequirements, rewardUsd),
    batchSize: numberOrUndefined(row.target_batch_size) ?? numberOrUndefined(metadata.batchSize),
    payoutUnit: row.payout_unit ? String(row.payout_unit) : metadata.payoutUnit ? String(metadata.payoutUnit) : undefined,
    metricCapUsd: numberOrUndefined(row.metric_cap_usd) ?? numberOrUndefined(metadata.metricCapUsd),
    proofPerTarget: Boolean(row.proof_per_target ?? metadata.proofPerTarget),
    cellName: metadata.cellName ? String(metadata.cellName) : undefined,
    managerHandle: metadata.managerHandle ? String(metadata.managerHandle) : undefined,
    controlledRun: mapControlledRun(row, metadata),
  };
}

function mapAssignment(row: Record<string, unknown>): TaskAssignment {
  const snapshot = row.snapshot as TaskOffer | undefined;
  if (snapshot) return {
      ...snapshot,
      assignmentId: String(row.assignment_id),
      state: row.state as TaskAssignment["state"],
      acceptedAt: String(row.accepted_at),
      progress: (row.progress as TaskAssignment["progress"]) ?? {},
      evidence: (row.evidence as TaskAssignment["evidence"]) ?? {},
      targets: (row.targets as AssignmentTarget[]) ?? [],
      reviewerNote: row.reviewer_note ? String(row.reviewer_note) : undefined,
    };
  const rawSteps = asArray(row.steps);
  const graph = asObject(row.playbook_graph);
  const metadata = asObject(graph.metadata);
  const steps = rawSteps.map((item) => {
    const step = asObject(item);
    return {
      key: String(step.key),
      title: String(step.title),
      instruction: String(step.instructions ?? ""),
      required: step.required !== false,
      inputType: normalizeInputType(step.inputType),
      example: step.example ? String(step.example) : undefined,
    };
  });
  const progressEntries: Array<[string, string | number | boolean]> = rawSteps.map((item) => {
    const step = asObject(item);
    const value = typeof step.value === "string" || typeof step.value === "number" || typeof step.value === "boolean"
      ? step.value
      : Boolean(step.completed);
    return [String(step.key), value];
  });
  const progress = Object.fromEntries(progressEntries);
  const evidenceRequirements = collectEvidence(rawSteps);
  const rewardUsd = Number(row.reward_max ?? 0);
  const targets = mapAssignmentTargets(row.targets);
  return {
    assignmentId: String(row.assignment_id),
    id: String(row.assignment_id),
    taskId: String(row.workflow_id),
    title: String(row.brand ?? row.playbook_title ?? "House task"),
    summary: String(row.public_summary ?? ""),
    route: (graph.route ?? "closer") as TaskOffer["route"],
    outcome: (graph.outcome ?? "demo_calls") as TaskOffer["outcome"],
    rewardUsd,
    estimatedMinutes: Number(metadata.estimatedMinutes ?? Math.max(20, steps.length * 15)),
    dueAt: String(row.due_at),
    matched: true,
    sopTitle: String(row.playbook_title ?? "Task SOP"),
    sopVersion: Number(graph.version ?? 1),
    resources: collectResources(rawSteps),
    steps,
    evidenceRequirements,
    state: row.state as TaskAssignment["state"],
    acceptedAt: String(row.claimed_at),
    progress,
    evidence: readEvidenceDraft(String(row.assignment_id)),
    reviewerNote: row.reviewer_note ? String(row.reviewer_note) : undefined,
    product: mapProduct(row, metadata),
    compensation: mapCompensation(metadata, evidenceRequirements, rewardUsd),
    targets,
    batchSize: numberOrUndefined(row.target_batch_size) ?? (targets.length > 0 ? targets.length : undefined),
    payoutUnit: row.payout_unit ? String(row.payout_unit) : metadata.payoutUnit ? String(metadata.payoutUnit) : undefined,
    metricCapUsd: numberOrUndefined(row.metric_cap_usd) ?? numberOrUndefined(metadata.metricCapUsd),
    proofPerTarget: Boolean(row.proof_per_target ?? metadata.proofPerTarget ?? targets.length > 0),
    cellName: metadata.cellName ? String(metadata.cellName) : undefined,
    managerHandle: metadata.managerHandle ? String(metadata.managerHandle) : undefined,
    controlledRun: mapControlledRun(row, metadata),
  };
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function mapAssignmentTargets(value: unknown): AssignmentTarget[] {
  return asArray(value).map((raw, index) => {
    const item = asObject(raw);
    const route = ["clipper", "seeder", "closer", "scout", "promoter"].includes(String(item.route)) ? String(item.route) as AssignmentTarget["route"] : "scout";
    const sourceObject = ["prospects", "content", "influencers", "similar_companies", "manual"].includes(String(item.sourceObject)) ? String(item.sourceObject) as AssignmentTarget["sourceObject"] : "manual";
    const status = ["pending", "submitted", "approved", "rejected"].includes(String(item.status)) ? String(item.status) as AssignmentTarget["status"] : "pending";
    const resultType = ["submitted", "qualified_lead", "approved_record", "interested_reply", "qualified_reply", "booked_call", "approved_placement", "approved_asset", "verified_metric"].includes(String(item.resultType))
      ? String(item.resultType) as AssignmentTarget["resultType"]
      : "submitted";
    const latestEvent = ["researched", "contacted", "replied", "booked", "published", "views_checked", "proof_added"].includes(String(item.latestEvent))
      ? String(item.latestEvent) as AssignmentTarget["latestEvent"]
      : undefined;
    return {
      id: String(item.id ?? crypto.randomUUID()),
      targetId: String(item.targetId ?? item.target_id ?? item.id ?? crypto.randomUUID()),
      position: Number(item.position ?? index),
      sourceObject,
      productName: String(item.productName ?? "InferenceSaver"),
      route,
      targetName: String(item.targetName ?? "Target"),
      targetUrl: item.targetUrl ? String(item.targetUrl) : undefined,
      maskedContact: item.maskedContact ? String(item.maskedContact) : undefined,
      contactHint: item.contactHint ? String(item.contactHint) : undefined,
      context: String(item.context ?? ""),
      icpReason: String(item.icpReason ?? ""),
      status,
      resultType,
      latestEvent,
      latestMetricViews: numberOrUndefined(item.latestMetricViews),
      reviewerNotes: item.reviewerNotes ? String(item.reviewerNotes) : undefined,
    };
  });
}

function normalizeInputType(value: unknown): TaskOffer["steps"][number]["inputType"] {
  return value === "text" || value === "url" || value === "number" ? value : "check";
}

function mapGraphStep(value: unknown): TaskOffer["steps"][number] {
  const node = asObject(value);
  return {
    key: String(node.key ?? node.taskKey ?? crypto.randomUUID()),
    title: String(node.title ?? "Complete task step"),
    instruction: String(node.instruction ?? node.instructions ?? ""),
    required: node.required !== false,
    inputType: normalizeInputType(node.inputType),
    example: node.example ? String(node.example) : undefined,
  };
}

function collectEvidence(nodes: unknown[]): TaskOffer["evidenceRequirements"] {
  const found = nodes.flatMap((value) => {
    const node = asObject(value);
    return asArray(node.evidenceRules).map((raw) => {
      const item = asObject(raw);
      const kind = ["text", "url", "metric", "screenshot", "file"].includes(String(item.kind)) ? String(item.kind) as TaskOffer["evidenceRequirements"][number]["kind"] : "text";
      return {
        key: String(item.key ?? crypto.randomUUID()),
        label: String(item.label ?? "Task proof"),
        kind,
        required: item.required !== false,
        help: String(item.help ?? item.instruction ?? "Add verifiable proof."),
        allowedMime: asArray(item.allowedMime).map(String),
      };
    });
  });
  return found.length ? found : [{ key: "result_note", label: "Result summary", kind: "text", required: true, help: "Describe what you completed and the result." }];
}

function collectResources(nodes: unknown[]): TaskOffer["resources"] {
  return nodes.flatMap((value) => {
    const node = asObject(value);
    return asArray(node.resources).map((raw) => {
      const item = asObject(raw);
      const rawKind = String(item.kind ?? item.type ?? "download");
      return {
        id: String(item.id ?? item.key ?? crypto.randomUUID()),
        label: String(item.label ?? item.title ?? "Open resource"),
        kind: (["pdf", "drive", "download", "template"].includes(rawKind) ? rawKind : "download") as TaskOffer["resources"][number]["kind"],
        url: String(item.url ?? item.sourceUrl ?? "#"),
      };
    });
  });
}

function mapProduct(row: Record<string, unknown>, metadata: Record<string, unknown>): ProductBrief {
  const product = asObject(metadata.product);
  return {
    productName: String(product.productName ?? metadata.productName ?? "Ready-made product"),
    offer: String(product.offer ?? metadata.offer ?? row.deliverable ?? "Approved offer to promote"),
    targetCustomer: String(product.targetCustomer ?? metadata.targetCustomer ?? row.market ?? "Matched customer segment"),
    objective: String(product.objective ?? metadata.objective ?? row.public_summary ?? "Move the opportunity to the next verified step."),
  };
}

function mapCompensation(
  metadata: Record<string, unknown>,
  evidenceRequirements: TaskOffer["evidenceRequirements"],
  rewardUsd: number,
): CompensationModel {
  const compensation = asObject(metadata.compensation);
  const type = ["fixed", "metric", "milestone", "client_close"].includes(String(compensation.type))
    ? String(compensation.type) as CompensationModel["type"]
    : "fixed";
  return {
    type,
    fixedUsd: numberOrUndefined(compensation.fixedUsd) ?? rewardUsd,
    metricUnit: compensation.metricUnit ? String(compensation.metricUnit) : undefined,
    rateUsd: numberOrUndefined(compensation.rateUsd),
    capUsd: numberOrUndefined(compensation.capUsd) ?? rewardUsd,
    clientValueUsd: numberOrUndefined(compensation.clientValueUsd),
    managerOverridePercent: numberOrUndefined(compensation.managerOverridePercent) ?? 4,
    reviewerBonusUsd: numberOrUndefined(compensation.reviewerBonusUsd),
    proofKey: String(compensation.proofKey ?? evidenceRequirements[0]?.key ?? "result_note"),
    description: String(compensation.description ?? "Paid only after required proof is approved."),
  };
}

function numberOrUndefined(value: unknown) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function mapControlledRun(row: Record<string, unknown>, metadata: Record<string, unknown>): ControlledRunMetadata | undefined {
  const run = asObject(metadata.controlledRun);
  const runType = controlledRunType(row.run_type ?? run.runType);
  const riskLevel = controlledRunRisk(row.risk_level ?? run.riskLevel);
  const reviewMode = controlledReviewMode(row.review_mode ?? run.reviewMode);
  const autoDecision = proofAutoDecision(row.auto_decision ?? run.autoDecision);
  const proofChecks = asObject(row.proof_checks ?? run.proofChecks);
  const mapped: ControlledRunMetadata = {
    runId: stringOrUndefined(row.run_id ?? run.runId),
    runType,
    riskLevel,
    reviewMode,
    runBudgetUsd: numberOrUndefined(row.run_budget_usd ?? run.runBudgetUsd),
    dailyBudgetUsd: numberOrUndefined(row.daily_budget_usd ?? run.dailyBudgetUsd),
    autoApproveThreshold: numberOrUndefined(row.auto_approve_threshold ?? run.autoApproveThreshold),
    auditSamplePercent: numberOrUndefined(row.audit_sample_percent ?? run.auditSamplePercent),
    proofChecks: Object.keys(proofChecks).length ? Object.fromEntries(Object.entries(proofChecks).map(([key, value]) => [key, Boolean(value)])) : undefined,
    autoDecision,
    autoScore: numberOrUndefined(row.auto_score ?? run.autoScore),
    reviewRequiredReason: stringOrUndefined(row.review_required_reason ?? run.reviewRequiredReason),
  };
  return Object.values(mapped).some((value) => value !== undefined) ? mapped : undefined;
}

function stringOrUndefined(value: unknown) {
  if (value == null || value === "") return undefined;
  return String(value);
}

function controlledRunType(value: unknown): ControlledRunType | undefined {
  return ["lead_finder", "lead_qualifier", "follow_up_assistant", "demo_booking"].includes(String(value))
    ? String(value) as ControlledRunType
    : undefined;
}

function controlledRunRisk(value: unknown): ControlledRunRisk | undefined {
  return ["low", "medium", "high"].includes(String(value)) ? String(value) as ControlledRunRisk : undefined;
}

function controlledReviewMode(value: unknown): ControlledReviewMode | undefined {
  return ["auto_sample", "manager_review", "admin_review"].includes(String(value)) ? String(value) as ControlledReviewMode : undefined;
}

function proofAutoDecision(value: unknown): ProofAutoDecision | undefined {
  return ["green", "yellow", "red"].includes(String(value)) ? String(value) as ProofAutoDecision : undefined;
}

function readEvidenceDraft(assignmentId: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(`house-evidence-${assignmentId}`) ?? "{}");
  } catch {
    return {};
  }
}
